import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ProjectCard from "../Projects/ProjectCards";
import './Work.css';

// Import project images
import bigStompLogo from "../../Assets/Projects/big-stomp-logo.png";
import generati0nLogo from "../../Assets/Projects/generati0n-logo.jpg";
import grajalesCleaningLogo from "../../Assets/Projects/grajales-cleaning-logo.png";

// Import tech icons
import {
  DiJava,
  DiPython,
  DiJavascript1,
  DiReact,
  DiAngularSimple,
  DiDocker,
  DiGit,
} from "react-icons/di";
import {
  SiKubernetes,
  SiSpringboot,
  SiApachekafka,
  SiHelm,
  SiAzuredevops,
  SiRedhatopenshift,
  SiMicrosoftazure,
  SiGooglecloud,
  SiAmazonaws,
  SiFlask,
  SiPostgresql,
  SiMongodb,
  SiOracle,
  SiSelenium,
  SiSonarqube,
  SiGithubactions,
  SiJenkins,
  SiTerraform,
  SiTypescript,
  SiAngular,
  SiJfrog,
  SiJunit5
} from "react-icons/si";
import { FaLock } from "react-icons/fa";

// Technology icon mapping
const techIconMap = {
  'kubernetes': <SiKubernetes />,
  'openshift': <SiRedhatopenshift />,
  'azure devops': <SiAzuredevops />,
  'argocd': <SiKubernetes />,
  'helm': <SiHelm />,
  'kustomize': <SiKubernetes />,
  'hashicorp vault': <FaLock />,
  'istio': <SiKubernetes />,
  'spring boot': <SiSpringboot />,
  'java': <DiJava />,
  'apache camel': <SiApachekafka />,
  'oauth2': <FaLock />,
  'tls': <FaLock />,
  'sonarqube': <SiSonarqube />,
  'jfrog artifactory': <SiJfrog />,
  'junit': <SiJunit5 />,
  'citrus': <SiJunit5 />,
  'activemq': <SiApachekafka />,
  'selenium': <SiSelenium />,
  'python': <DiPython />,
  'javascript': <DiJavascript1 />,
  'typescript': <SiTypescript />,
  'react': <DiReact />,
  'angular': <SiAngular />,
  'docker': <DiDocker />,
  'git': <DiGit />,
  'azure': <SiMicrosoftazure />,
  'gcp': <SiGooglecloud />,
  'aws': <SiAmazonaws />,
  'flask': <SiFlask />,
  'postgresql': <SiPostgresql />,
  'mongodb': <SiMongodb />,
  'oracle': <SiOracle />,
  'github actions': <SiGithubactions />,
  'jenkins': <SiJenkins />,
  'terraform': <SiTerraform />,
  'sql server': <SiPostgresql />
};

function getTechIcon(techName) {
  const normalizedName = techName.toLowerCase().trim();
  return techIconMap[normalizedName] || null;
}

function TechnologyIcons({ technologies }) {
  const techList = technologies.split(',').map(tech => tech.trim());
  // Remove duplicates by converting to Set and back to array
  const uniqueTechList = [...new Set(techList.map(tech => tech.toLowerCase()))];
  
  return (
    <div className="work-tech-icons">
      {uniqueTechList.map((tech, index) => {
        const icon = getTechIcon(tech);
        if (!icon) return null;
        
        // Find the original case version for display
        const originalTech = techList.find(t => t.toLowerCase() === tech) || tech;
        
        return (
          <div key={index} className="work-tech-icon-wrapper" title={originalTech}>
            {icon}
            <span className="work-tech-icon-label">{originalTech}</span>
          </div>
        );
      })}
    </div>
  );
}

function Work() {
  const [markdown, setMarkdown] = useState('');

  useEffect(() => {
    fetch('/experience.md')
      .then(response => response.text())
      .then(text => setMarkdown(text))
      .catch(error => console.error('Error loading experience content:', error));
  }, []);

  return (
    <Container fluid className="work-section" id="work">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="animate-fade-in-up">
              <h1 className="project-heading text-center mb-5">
                <strong className="text-gradient">Work</strong> & Projects
              </h1>
            </div>
            
            {/* Experience Timeline */}
            <div className="work-experience-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 className="work-experience-title" {...props} />,
                  h2: ({node, ...props}) => <h2 className="work-experience-heading" {...props} />,
                  h3: ({node, ...props}) => <h3 className="work-experience-role" {...props} />,
                  hr: ({node, ...props}) => <div className="work-experience-divider" {...props} />,
                  ul: ({node, ...props}) => <ul className="work-experience-list" {...props} />,
                  strong: ({node, ...props}) => <strong className="work-experience-strong" {...props} />,
                  em: ({node, ...props}) => <em className="work-experience-date" {...props} />,
                  a: ({node, ...props}) => <a className="work-experience-link" target="_blank" rel="noopener noreferrer" {...props} />,
                  p: ({node, children, ...props}) => {
                    // Convert all children to text for pattern matching
                    const text = React.Children.toArray(children).map(child => {
                      if (typeof child === 'string') return child;
                      if (React.isValidElement(child) && child.props.children) {
                        if (typeof child.props.children === 'string') {
                          return child.props.children;
                        }
                        return React.Children.toArray(child.props.children).join('');
                      }
                      return '';
                    }).join('');
                    
                    // Check if this is a Technologies paragraph
                    if (text.includes('Technologies:')) {
                      // Extract the technologies list - handle both bold and non-bold versions
                      const techMatch = text.match(/Technologies:\s*(.+)/);
                      if (techMatch && techMatch[1]) {
                        return <TechnologyIcons technologies={techMatch[1]} />;
                      }
                    }
                    
                    if (text.includes('•') && (text.includes('Louisville') || text.includes('Present'))) {
                      return <p className="work-experience-company" {...props}>{children}</p>;
                    }
                    if (text.includes('Key Achievements:')) {
                      return <p className="work-experience-achievements-label" {...props}>{children}</p>;
                    }
                    
                    return <p className="work-experience-description" {...props}>{children}</p>;
                  }
                }}
              >
                {markdown}
              </ReactMarkdown>
            </div>

            {/* Projects Grid Section */}
            <div className="work-projects-section mt-5">
              <div className="animate-fade-in-up">
                <h2 className="work-projects-heading text-center mb-4">
                  <strong className="text-gradient">Featured</strong> Projects
                </h2>
              </div>
              
              <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
                <Col md={4} className="project-card">
                  <ProjectCard
                    imgPath={generati0nLogo}
                    isBlog={false}
                    title="Generati0n"
                    description="AI-powered automation tool that streamlines repetitive tasks using advanced machine learning algorithms. Built with modern web technologies for seamless user experience."
                  />
                </Col>

                <Col md={4} className="project-card">
                  <ProjectCard
                    imgPath={bigStompLogo}
                    isBlog={false}
                    title="Big Stomp"
                    description="Music festival discovery and management platform. Features event tracking, artist lineups, and social integration for music enthusiasts."
                  />
                </Col>

                <Col md={4} className="project-card">
                  <ProjectCard
                    imgPath={grajalesCleaningLogo}
                    isBlog={false}
                    title="Grajales Cleaning"
                    description="Professional cleaning service website with booking system, service portfolio, and customer management. Responsive design optimized for local business needs."
                    demoLink="https://grajalescleaning.com"
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default Work;